/**
 * Черновик формы «+» (обращения: ошибка / идея / вопрос) — в localStorage браузера (владелец 03.11.2026:
 * «Олеся говорит, что отправила, а на доске нет» — с её компьютера ни одной отправки до сервера не дошло;
 * текст, набранный в шторке, терялся при закрытии / «Назад» / обновлении страницы без следа).
 * Зеркало frontend/src/lib/feedback-draft.ts (CRM) — правки вносить в оба места.
 * Чистые функции с подменяемым хранилищем — для node-тестов (frontend-wiki/src/lib/feedback-draft.test.ts).
 *
 * Ключ — на пользователя и вид обращения; формы с темой со страниц (preset) черновик не ведут.
 * Черновик живёт 7 дней, пустой (оба поля пустые) — удаляется. Любая ошибка хранилища
 * (приватный режим, квота) глотается: черновик — удобство, не обязанность.
 */
export interface FeedbackDraft {
  body: string;
  expected: string;
  /** Момент последней правки, мс. */
  savedAt: number;
}

export interface DraftStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const FEEDBACK_DRAFT_PREFIX = "fb-draft:";
export const FEEDBACK_DRAFT_TTL_MS = 7 * 86_400_000;

function defaultStorage(): DraftStorage | null {
  try {
    const s = (globalThis as { localStorage?: DraftStorage }).localStorage;
    return s ?? null;
  } catch {
    return null;
  }
}

export function feedbackDraftKey(userId: string | null | undefined, kind: string): string {
  return `${FEEDBACK_DRAFT_PREFIX}${userId || "anon"}:${kind}`;
}

/** Прочитать черновик: нет записи, битый JSON, не те поля или старше 7 дней → null (устаревший — удаляется). */
export function readFeedbackDraft(key: string, storage: DraftStorage | null = defaultStorage(), now = Date.now()): FeedbackDraft | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<FeedbackDraft> | null;
    if (!v || typeof v !== "object" || typeof v.body !== "string" || typeof v.savedAt !== "number") return null;
    if (now - v.savedAt > FEEDBACK_DRAFT_TTL_MS) {
      storage.removeItem(key);
      return null;
    }
    const body = v.body;
    const expected = typeof v.expected === "string" ? v.expected : "";
    if (!body.trim() && !expected.trim()) return null;
    return { body, expected, savedAt: v.savedAt };
  } catch {
    return null;
  }
}

/** Сохранить черновик; оба поля пустые → запись удаляется. */
export function writeFeedbackDraft(
  key: string,
  draft: Pick<FeedbackDraft, "body" | "expected">,
  storage: DraftStorage | null = defaultStorage(),
  now = Date.now(),
): void {
  if (!storage) return;
  try {
    if (!draft.body.trim() && !draft.expected.trim()) {
      storage.removeItem(key);
      return;
    }
    storage.setItem(key, JSON.stringify({ body: draft.body, expected: draft.expected, savedAt: now } satisfies FeedbackDraft));
  } catch {
    /* квота / приватный режим — черновик не сохраняем */
  }
}

export function clearFeedbackDraft(key: string, storage: DraftStorage | null = defaultStorage()): void {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    /* нет доступа к хранилищу */
  }
}
